import {Box, LogoSuite, Section, Text, Tiles} from '@primer/react-brand/esm'
import type {ReactElement, ReactNode} from 'react'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {Logo} from '../../../components/FlexSuiteLogos/Logo'
import type {IntroLogoType} from '../../../types/templates/templateFlexSuiteDetails'
import {CopilotCliPicker} from '../CopilotCliPicker'
import type {IntroSectionProps, LogobarOptions, LogoSuiteOptions} from './types'
import {getExperimentCommands} from './utils'
import {IntroIconCards, IntroImageCards, IntroPillars, IntroSimpleIntro, IntroStats} from './variants'
import styles from './IntroSection.module.css'

export function IntroSection({
  introLogoHeading,
  introLogoType,
  introLogos,
  introLogoLink,
  settings,
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading = true,
  hasMarqueeTopBorder = false,
}: IntroSectionProps) {
  const introContextContent = renderIntroContextContent({
    settings,
    introContext,
    introContextHeading,
    introContextLink,
    introContextBody,
    introItems,
    introIconCards,
    introPillars,
    introImageCards,
    introStats,
  })

  const hasIntroContent = Boolean(introLogos?.length) || introContextContent !== null

  if (!hasIntroContent) return null

  return (
    <Section fullWidth paddingBlockEnd="none" paddingBlockStart="none">
      {introLogos && introLogos.length > 0
        ? renderIntroLogos({
            introLogos,
            introLogoType,
            introLogoHeading,
            introLogoLink,
            logobarOptions,
            logoSuiteOptions,
            hideLogoHeading,
            hasMarqueeTopBorder,
          })
        : null}
      {introContextContent}
    </Section>
  )
}

type RenderIntroLogosArgs = {
  introLogos: string[]
  introLogoType: IntroLogoType | undefined
  introLogoHeading: IntroSectionProps['introLogoHeading']
  introLogoLink: IntroSectionProps['introLogoLink']
  logobarOptions: LogobarOptions | undefined
  logoSuiteOptions: LogoSuiteOptions | undefined
  hideLogoHeading: boolean
  hasMarqueeTopBorder: boolean
}

function renderIntroLogos({
  introLogos,
  introLogoType,
  introLogoHeading,
  introLogoLink,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading,
  hasMarqueeTopBorder,
}: RenderIntroLogosArgs): ReactElement {
  // Tiles is the non-motion default when the author has not picked a type.
  if (introLogoType !== 'Marquee') {
    return (
      <Box marginBlockStart={48} data-testid="intro-tiles">
        {introLogoHeading ? (
          hideLogoHeading ? (
            // Keep the heading announceable but don't reserve any visual space.
            <Text as="p" size="200" variant="muted" align="center" className="sr-only">
              {introLogoHeading}
            </Text>
          ) : (
            <Box paddingBlockEnd={{narrow: 'spacious', regular: 64}} paddingBlockStart="spacious">
              <Text as="p" size="200" variant="muted" align="center">
                {introLogoHeading}
              </Text>
            </Box>
          )
        ) : null}
        <Tiles variant="gridlines" layout="compact">
          {introLogos.map(logo => (
            <Tiles.Item key={logo} name={logo} data-testid={`intro-tile-${logo}`}>
              <Logo name={logo} />
            </Tiles.Item>
          ))}
        </Tiles>
      </Box>
    )
  }

  const takeoverButton =
    logobarOptions?.takeoverButton ??
    (introLogoLink?.fields.href ? {label: introLogoLink.fields.text, href: introLogoLink.fields.href} : undefined)

  return (
    <LogoSuite
      align="center"
      variant={logoSuiteOptions?.variant}
      className={hasMarqueeTopBorder ? styles.marqueeTopBorder : undefined}
    >
      <LogoSuite.Heading visuallyHidden={hideLogoHeading || !introLogoHeading}>
        {introLogoHeading || t('Logos')}
      </LogoSuite.Heading>
      <LogoSuite.Logobar
        variant={logobarOptions?.variant}
        marquee
        marqueeSpeed={logobarOptions?.marqueeSpeed}
        takeoverButton={takeoverButton}
      >
        {introLogos.map(logo => (
          <Logo key={logo} name={logo} />
        ))}
      </LogoSuite.Logobar>
    </LogoSuite>
  )
}

type IntroContextContentProps = Pick<
  IntroSectionProps,
  | 'settings'
  | 'introContext'
  | 'introContextHeading'
  | 'introContextLink'
  | 'introContextBody'
  | 'introItems'
  | 'introIconCards'
  | 'introPillars'
  | 'introImageCards'
  | 'introStats'
>

function renderIntroContextContent({
  settings,
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
}: IntroContextContentProps): ReactNode {
  if (!introContext) return null

  switch (introContext) {
    case 'Simple Intro':
      return (
        <IntroSimpleIntro
          introContextHeading={introContextHeading}
          introContextBody={introContextBody}
          introContextLink={introContextLink}
          introItems={introItems}
        />
      )
    case 'Pillars': {
      const experimentCommands = getExperimentCommands(settings)

      return (
        <div className={styles.pillarsWithCopilot}>
          {experimentCommands.length > 0 ? <CopilotCliPicker commands={experimentCommands} /> : null}
          <IntroPillars introPillars={introPillars} />
        </div>
      )
    }
    case 'Icon Cards':
      return <IntroIconCards introIconCards={introIconCards} />
    case 'Image Cards':
      return <IntroImageCards introImageCards={introImageCards} />
    case 'Stats':
      return <IntroStats introStats={introStats} />
    default:
      return null
  }
}
